import React, {
    Children, useRef, useCallback, useEffect, useState, ReactNode,
} from "react";
import { RiMoreFill } from "@remixicon/react";
import { DropdownProps } from "react-bootstrap";
import { ToggleProps } from "../theme/dropdown/Toggle";
import Dropdown from "../theme/dropdown/Dropdown";
import Style from "./OverflowMenu.module.scss";

export default function OverflowMenu({
    toggleSettings, dropdownClassName, placement, className, id, children,
}: {
    toggleSettings: Omit<ToggleProps, "hidden">;
    dropdownClassName?: string;
    placement?: DropdownProps["placement"];
    className?: string;
    id?: string;
    children: ReactNode;
}) {
    const indexRef = useRef<Set<number>>(new Set());
    const [hiddenCounter, setHiddenCounter] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
        const onContainer = Array.from(containerRef.current?.children ?? []);
        const onMenu = Array.from(menuRef.current?.children ?? []);

        entries.forEach(entry => {
            const index = onContainer.indexOf(entry.target as HTMLElement);
            onMenu[index].classList.toggle(Style.hidden, entry.isIntersecting);

            if (entry.isIntersecting) {
                indexRef.current.delete(index);
            } else {
                indexRef.current.add(index);
            }
        });

        setHiddenCounter(indexRef.current.size);
    }, []);

    useEffect(() => {
        indexRef.current = new Set();

        const observer = new IntersectionObserver(handleIntersection, { root: containerRef.current, threshold: 1 });
        Array.from(containerRef.current?.children ?? []).forEach(item => observer.observe(item));

        return () => observer.disconnect();
    }, [Children.count(children), containerRef.current]);

    return (
        <>
            <div ref={containerRef} className={`${Style.container} ${className}`} id={id}>
                {children}
            </div>
            <Dropdown className={dropdownClassName} placement={placement}>
                <Dropdown.Toggle prefixIcon={<RiMoreFill />} {...toggleSettings} hidden={hiddenCounter === 0} />
                <Dropdown.Menu ref={menuRef} renderOnMount>{children}</Dropdown.Menu>
            </Dropdown>
        </>
    );
}

OverflowMenu.defaultProps = {
    dropdownClassName: "",
    placement: "top-end",
    className: "",
    id: "",
};
